import { useCallback, useEffect, useRef } from 'react'
import {
  addProtocol, GeoJSONSource, Map as MlMap, NavigationControl, Popup, ScaleControl, setWorkerUrl,
  type MapLayerMouseEvent,
} from 'maplibre-gl'
// MapLibre 6 loads its worker as a separate module and resolves the URL itself, which
// Vite neither rewrites nor emits -- the request 404s, no worker starts, and the map
// renders an empty canvas with no error anywhere. `?worker&url` makes Vite *bundle* the
// worker and hand back its hashed path; plain `?url` is not enough, because that copies
// the file verbatim and its own bare imports then fail to resolve at the new location.
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'
import { Protocol } from 'pmtiles'
import 'maplibre-gl/dist/maplibre-gl.css'

import { freshCells, useFleet } from '../state/store'
import { locate } from '../lib/pointInPolygon'
import { resolveTimsTrip } from '../lib/timsTrip'
import { overrideProgressWithArrival } from '../lib/range'
import type { Alert, Vehicle, VehicleMeta } from '../lib/types'
import { buildStyle, CORRIDOR_BOUNDS, TILE_BOUNDS, type MapTheme } from './style'
import { rasterBasemap, styleKey, type BasemapMode } from './basemaps'
import {
  addReferenceLayers, updateReference,
  LYR_FENCE_FILL, LYR_FENCE_LINE, LYR_FENCE_BADGE, LYR_FENCE_LABEL,
  LYR_ROUTE_CASING, LYR_ROUTE_LINE, LYR_ROUTE_LINE_EMPTY,
} from './layers/reference'
import {
  addVehicleLayers, buildVehicleFeatures, setVehicleData,
  LYR_CLUSTER, LYR_VEHICLE, LYR_BADGE, LYR_FLAG, LYR_FLAG_LABEL, SRC_TRANSIT,
} from './layers/vehicles'
import { isUsableFixEpoch, pruneTweens, tweenedPosition, type TweenStore } from './layers/fixTween'
import { registerVehicleIcons } from './layers/markers'
import { buildVehicleFlags, mergeStickyFlags } from '../lib/riskFlags'
import { showVehiclePopup } from './vehiclePopup'
import {
  addTrackLayers, setTrackData,
  LYR_TRACK, LYR_TRACK_DIR, LYR_TRACK_ENDS, LYR_TRACK_HALO,
} from './layers/track'
import { addReplayAlertLayers, LYR_REPLAY_ALERTS, setReplayAlertData } from './layers/replayAlerts'
import type { PhotorealHandle } from './photoreal'
import {
  addHeatLayers, cellFeatures, eventFeatures, setHeatData, setHeatMode,
} from './layers/heatmap'

/** Live map chip (SPD/HRSH/!) clears fast -- a dispatcher needs "is it happening now",
 *  not a 45-minute history. The vehicle-detail drawer keeps the longer FLAG_WINDOW_MS. */
const MAP_FLAG_WINDOW_MS = 60 * 1000

/**
 * The map.
 *
 * React owns the container and nothing else. Every live update goes through an
 * animation-frame loop reading the store imperatively, because pushing forty position
 * updates a second through reconciliation is how a map drops to twelve frames a second.
 * The component re-renders on selection, never on telemetry.
 */

const CONTEXT_LAYERS = [
  LYR_FENCE_FILL, LYR_FENCE_LINE, LYR_FENCE_BADGE, LYR_FENCE_LABEL,
  LYR_ROUTE_CASING, LYR_ROUTE_LINE, LYR_ROUTE_LINE_EMPTY,
  LYR_TRACK_HALO, LYR_TRACK, LYR_TRACK_DIR, LYR_TRACK_ENDS,
]

/** Full-map live view: trucks only — hide fences, routes, tracks, and heat. */
function applyLiveOnly(map: MlMap, liveOnly: boolean): void {
  for (const id of CONTEXT_LAYERS) {
    if (map.getLayer(id)) {
      map.setLayoutProperty(id, 'visibility', liveOnly ? 'none' : 'visible')
    }
  }
  const st = useFleet.getState()
  setHeatMode(map, liveOnly ? 'off' : st.heatMode)
  setTrackData(map, liveOnly ? null : st.track)
}

/**
 * The camera limits belong to the basemap, not to the map.
 *
 * The vector archive is an extract: cut to TILE_BOUNDS and stopped at z14, so panning or
 * zooming past it renders blank tiles that read as a broken map. The raster services are
 * global and go deeper, so imposing the extract's limits on them would be inventing a
 * restriction that nothing in the data justifies -- and would block the one job satellite
 * imagery is switched on for, which is zooming into a yard.
 */
function applyCameraLimits(map: MlMap, mode: BasemapMode): void {
  const raster = rasterBasemap(mode)
  if (raster) {
    map.setMaxBounds(null)
    map.setMaxZoom(raster.maxzoom)
    return
  }
  map.setMaxZoom(16)
  map.setMaxBounds(TILE_BOUNDS)
}

// pmtiles:// has to be registered before any Map is constructed, and exactly once per
// page -- registering twice throws.
let protocolRegistered = false
function ensureProtocol(): void {
  if (protocolRegistered) return
  setWorkerUrl(workerUrl)
  const protocol = new Protocol()
  addProtocol('pmtiles', protocol.tile)
  protocolRegistered = true
}

export function MapCanvas({
  replayFrame, replayMeta, replayAlerts, theme = 'dark', cameraFollow = false, onUserCamera,
  liveOnly = false,
}: {
  replayFrame?: Vehicle[] | null
  replayMeta?: ReadonlyMap<string, VehicleMeta> | null
  /** This one replay's own alerts (TripReplay.tsx's `tripReplayAlerts`, already scoped to
   *  the vehicle + time window) -- a discrete marker layer, separate from the always-on
   *  live alert heatmap. Omit entirely outside a trip replay. */
  replayAlerts?: Alert[] | null
  theme?: MapTheme
  /** When true, keep the truck centered. User pan/zoom turns this off via onUserCamera. */
  cameraFollow?: boolean
  onUserCamera?: () => void
  /** Live GPS fleet only — no heat, geofences, tracks, or replay overlay. */
  liveOnly?: boolean
} = {}): JSX.Element {
  const container = useRef<HTMLDivElement>(null)
  const mapRef = useRef<MlMap | null>(null)
  const replayRef = useRef<Vehicle[] | null>(null)
  const replayMetaRef = useRef<ReadonlyMap<string, VehicleMeta> | null>(null)
  const replayAlertsRef = useRef<Alert[] | null>(null)
  const replayTick = useRef(0)
  const followRef = useRef(false)
  const onUserCameraRef = useRef(onUserCamera)
  /** Whether the *currently applied* style was built with the tile archive. */
  const appliedTiles = useRef<string | null>(null)
  /** Whether the style has finished loading and its layers have been re-added. */
  const styleReady = useRef(false)
  const themeRef = useRef<MapTheme>(theme)
  const photoreal = useRef<PhotorealHandle | null>(null)
  // Consecutive google2d tile failures since the last reset -- see the 'error' handler in
  // the construction effect below for why this triggers an automatic fallback to 'vector'.
  const google2dFailures = useRef(0)
  const popupRef = useRef<Popup | null>(null)
  const flagsRef = useRef<ReturnType<typeof buildVehicleFlags>>(new Map())
  // Real-fix-to-real-fix lerp state (fixTween.ts), mount-scoped like the RAF loop that
  // reads it -- replaces the old coastPosition dead-reckoning guess.
  const tweenStoreRef = useRef<TweenStore>(new Map())
  const liveOnlyRef = useRef(liveOnly)

  // -------------------------------------------------------------------- construction
  useEffect(() => {
    if (!container.current || mapRef.current) return
    ensureProtocol()

    const { hasTiles: initialTiles, tilesVersion, basemapMode } = useFleet.getState()
    appliedTiles.current = `${initialTiles}|${tilesVersion}|${theme}|${styleKey(basemapMode)}`

    const map = new MlMap({
      container: container.current,
      style: buildStyle(initialTiles, tilesVersion, themeRef.current, basemapMode),
      bounds: CORRIDOR_BOUNDS,
      fitBoundsOptions: { padding: 48 },
      maxBounds: TILE_BOUNDS,
      // The archive stops at z14; letting the user zoom past it shows blank tiles and
      // reads as a broken map rather than as a deliberate limit.
      maxZoom: 16,
      minZoom: 5,
      attributionControl: { compact: true },
      dragRotate: false,
      pitchWithRotate: false,
    })
    // Deliberate debug handle. A blank map has no stack trace and no failing test; being
    // able to ask the live instance what its style and sources actually are is the
    // difference between diagnosing it in a minute and guessing for an hour.
    ;(window as unknown as { __map?: MlMap }).__map = map
    map.on('error', (e) => {
      console.error('[map]', e.error?.message ?? e)
      // 'google2d' is now the DEFAULT basemap (see the Google 2D tiles effect below), not
      // a manual opt-in like the other raster options -- a network hiccup or an
      // expired/exhausted session here is the console's own fault, not something the
      // operator chose knowing it needs internet, so it self-heals back to the offline
      // archive instead of leaving a blank map with no explanation. Threshold 3, not 1: a
      // lone edge-tile 404 at the grid boundary is normal and not worth tearing down a
      // working session over.
      const evt = e as unknown as { sourceId?: string }
      if (evt.sourceId !== 'basemap' || useFleet.getState().basemapMode !== 'google2d') {
        google2dFailures.current = 0
        return
      }
      google2dFailures.current += 1
      if (google2dFailures.current < 3) return
      google2dFailures.current = 0
      useFleet.getState().addBanner({
        id: 'google2d-failed', tone: 'warn',
        message: 'Google Maps tiles stopped loading — switched to the offline map.',
      })
      useFleet.getState().setBasemapMode('vector')
    })

    map.touchZoomRotate.disableRotation()
    map.addControl(new NavigationControl({ showCompass: false }), 'top-right')
    map.addControl(new ScaleControl({ maxWidth: 110, unit: 'metric' }), 'bottom-left')
    // Pan/zoom stay on during trip replay. Follow is a camera choice, not an interaction lock.
    map.dragPan.enable()
    map.scrollZoom.enable()
    map.touchZoomRotate.enable()
    map.touchZoomRotate.disableRotation()

    const releaseFollow = (): void => {
      if (!replayRef.current?.length || !followRef.current) return
      followRef.current = false
      map.stop()
      onUserCameraRef.current?.()
    }
    map.on('mousedown', releaseFollow)
    map.on('touchstart', releaseFollow)
    map.on('wheel', releaseFollow)
    // The constructor above hard-codes the vector archive's limits, which is right for the
    // default mode and wrong for any other. Reconcile once, so construction and restyling
    // go through the same rule rather than agreeing by coincidence.
    applyCameraLimits(map, basemapMode)
    mapRef.current = map

    // The constructor fits bounds against whatever the container measures at that instant,
    // which inside a flex row is usually not its final width -- the corridor ends up
    // off-centre. Re-fit once on load, and keep the map sized to the panel after that.
    map.once('load', () => {
      map.resize()
      map.fitBounds(CORRIDOR_BOUNDS, { padding: 48, duration: 0 })
    })
    const ro = new ResizeObserver(() => map.resize())
    ro.observe(container.current)

    const onStyle = (): void => {
      registerVehicleIcons(map)
      const { geofences, routes } = useFleet.getState()
      if (geofences && routes) {
        addReferenceLayers(map, {
          geofences, routes,
          counts: { geofences: 0, routes: 0 }, tiles: false,
        })
      }
      // Vehicles first so the heat layers have an anchor to sit under: heat is context,
      // never the subject, and must not draw over a truck.
      addVehicleLayers(map)
      addHeatLayers(map, LYR_CLUSTER)
      // Alert dots added BEFORE the track line (round-3 adversarial review, LOW): the
      // track line's own anchor (LYR_REPLAY_ALERTS) has to already exist, or addTrackLayers
      // falls back to no anchor and lands at the absolute top again, over everything.
      addReplayAlertLayers(map, LYR_VEHICLE)
      addTrackLayers(map, LYR_REPLAY_ALERTS)
      applyLiveOnly(map, liveOnlyRef.current)
      styleReady.current = true
      syncStyle()
    }
    map.on('style.load', onStyle)

    // ------------------------------------------------------------------ interactions
    const selectVehicle = useFleet.getState().selectVehicle
    const selectFence = useFleet.getState().selectFence

    const pickVehicle = (e: MapLayerMouseEvent): void => {
      const f = e.features?.[0]
      const id = f?.properties?.['vehicle_id']
      if (!f || typeof id !== 'string') return
      selectVehicle(id)
      const st = useFleet.getState()
      const lngLat = (f.geometry as GeoJSON.Point).coordinates as [number, number]
      popupRef.current = showVehiclePopup(map, id, lngLat, {
        vehicle: st.vehicles.get(id),
        meta: st.meta.get(id),
        trip: resolveTimsTrip(id, st.timsTrips),
        progress: overrideProgressWithArrival(id, st.progress.get(id), st.arrivalBoard.get(id), Date.now()),
        flag: flagsRef.current.get(id),
      }, popupRef.current)
    }
    map.on('click', LYR_VEHICLE, pickVehicle)
    map.on('click', 'veh-point-hub', pickVehicle)
    map.on('click', LYR_FLAG, pickVehicle)
    map.on('click', LYR_FLAG_LABEL, pickVehicle)

    map.on('click', LYR_FENCE_FILL, (e: MapLayerMouseEvent) => {
      const id = e.features?.[0]?.properties?.['geofence_id']
      if (typeof id === 'string') selectFence(id)
    })

    // A cluster is an invitation to zoom — unless leaves are already co-located, in which
    // case expansion lands on one stacked icon. Prefer the ring of leaves (spreadCoincident
    // separates them); if still inseparable, open the first plate so the click is not a no-op.
    map.on('click', LYR_CLUSTER, (e: MapLayerMouseEvent) => {
      const f = e.features?.[0]
      const clusterId = f?.properties?.['cluster_id']
      const src = map.getSource(SRC_TRANSIT)
      if (!f || typeof clusterId !== 'number' || !(src instanceof GeoJSONSource)) return
      const center = (f.geometry as GeoJSON.Point).coordinates as [number, number]
      void Promise.all([
        src.getClusterExpansionZoom(clusterId),
        src.getClusterLeaves(clusterId, 80, 0),
      ]).then(([zoom, leaves]) => {
        const z = Math.max(zoom ?? map.getZoom(), 13.5)
        map.easeTo({ center, zoom: z })
        if (leaves.length === 1) {
          const id = leaves[0]?.properties?.['vehicle_id']
          if (typeof id === 'string') selectVehicle(id)
        }
      })
    })

    map.on('click', LYR_BADGE, (e: MapLayerMouseEvent) => {
      const f = e.features?.[0]
      if (!f) return
      const id = f.properties?.['geofence_id']
      if (typeof id === 'string') selectFence(id)
      map.easeTo({ center: (f.geometry as GeoJSON.Point).coordinates as [number, number], zoom: 14 })
    })

    for (const layer of [
      LYR_VEHICLE, 'veh-point-hub', LYR_CLUSTER, LYR_BADGE, LYR_FLAG, LYR_FLAG_LABEL, LYR_FENCE_FILL,
    ]) {
      map.on('mouseenter', layer, () => { map.getCanvas().style.cursor = 'pointer' })
      map.on('mouseleave', layer, () => { map.getCanvas().style.cursor = '' })
    }

    return () => {
      popupRef.current?.remove()
      photoreal.current?.destroy()
      ro.disconnect()
      map.remove()
      mapRef.current = null
    }
  }, [])

  // ------------------------------------- restyle once the tile archive is known to exist
  // The map is built before /api/geo answers, so the first style has no basemap. Swapping
  // it in has to wait for the current style to finish loading -- calling setStyle mid-load
  // makes MapLibre discard its diff and rebuild, silently dropping every layer added by
  // the style.load handler.
  //
  // Waiting on 'idle' is the obvious way to do that and it deadlocks: the SSE firehose
  // calls setData continuously, so a live map is never idle and 'idle' never fires. Track
  // readiness explicitly off style.load instead, and reconcile desired vs applied.
  const syncStyle = useCallback((): void => {
    const map = mapRef.current
    if (!map || !styleReady.current) return
    const { hasTiles, tilesVersion, basemapMode } = useFleet.getState()
    const want = `${hasTiles}|${tilesVersion}|${themeRef.current}|${styleKey(basemapMode)}`
    if (appliedTiles.current === want) return
    appliedTiles.current = want
    styleReady.current = false          // style.load will set it again
    applyCameraLimits(map, basemapMode)
    map.setStyle(buildStyle(hasTiles, tilesVersion, themeRef.current, basemapMode))
  }, [])

  useEffect(() => useFleet.subscribe(syncStyle), [syncStyle])

  // ------------------------------------------------------------------ reference geometry
  useEffect(() =>
    useFleet.subscribe((s, prev) => {
      const map = mapRef.current
      if (!map || !s.geofences || !s.routes) return
      if (s.geofences === prev.geofences && s.routes === prev.routes) return
      // Same reasoning as the tick: readiness is "layers exist", not "every tile is in".
      if (!styleReady.current) return
      addReferenceLayers(map, {
        geofences: s.geofences, routes: s.routes,
        counts: { geofences: 0, routes: 0 }, tiles: s.hasTiles,
      })
      updateReference(map, {
        geofences: s.geofences, routes: s.routes,
        counts: { geofences: 0, routes: 0 }, tiles: s.hasTiles,
      })
    }), [])

  // --------------------------------------------------------- live positions, on a frame
  useEffect(() => {
    let raf = 0
    let lastRevision = -1
    // Gate for the flags/heat/alert-event recompute only (adversarial review, Phase 4):
    // dropping the live redraw key's 250ms throttle (AC-9) was meant to unlock the per-
    // vehicle position tween to a real 60fps glide, not to also re-run buildVehicleFlags/
    // mergeStickyFlags/freshCells/eventFeatures -- none of which changed cost and all of
    // which still only need to update at the old human-perceptible cadence, not 15x more
    // often. Deliberately a differently-named variable from the removed per-frame
    // position throttle (MapCanvas.test.ts's own AC-9 guard pins that removal, not a ban
    // on throttling something else entirely).
    let lastHeavyRecomputeBucket = -1
    const HEAVY_RECOMPUTE_MS = 250

    const tick = (): void => {
      raf = requestAnimationFrame(tick)
      const map = mapRef.current
      // NOT isStyleLoaded(): that also requires every source to be finished, and a
      // vector basemap streaming tiles keeps it false for as long as the operator keeps
      // panning. Gating on it means the fleet silently stops updating on the busiest
      // maps. All this needs is that the layers exist, which is what styleReady tracks.
      if (!map || !styleReady.current) return

      const s = useFleet.getState()
      const liveOnly = liveOnlyRef.current
      const replay = liveOnly ? null : replayRef.current
      // Replaying: the playhead is the clock, so redraw whenever it moves. Live: recompute
      // every animation frame (no throttle) so the tween (fixTween.ts) glides continuously
      // between real fixes instead of reading as discrete hops.
      const key = replay ? -replay.length - replayTick.current : Date.now()
      if (key === lastRevision) return
      lastRevision = key

      const centroids = (s.geofences?.features ?? []).map((f) => ({
        id: f.properties.geofence_id,
        name: f.properties.name,
        type: String(f.properties.type),
        lon: f.properties.centroid_lon,
        lat: f.properties.centroid_lat,
      }))
      // s.vehicles is shared across every non-diverted fleet (Ultratech + Wonder Cement,
      // as of 2026-10) -- this live fallback is Ultratech-only in practice (the only real
      // caller is CommandCentre.tsx; every other fleet's map -- BusRouteMap, GlobalMap --
      // is its own separate implementation, not this component), so filter explicitly
      // here rather than assume the store holds only Ultratech. If this component ever
      // grows a second live (non-replay) caller for another fleet, this needs a prop
      // instead of a hardcoded fleet id.
      // Captured BEFORE the fence-focus filter below narrows `list` -- pruneTweens must
      // see every vehicle actually still live in the fleet, not just the ones the current
      // UI focus happens to be showing, or selecting/deselecting a geofence would wipe
      // and resnap every truck outside it on every toggle (adversarial review, Phase 4).
      const fullRoster = replay ?? [...s.vehicles.values()].filter(
        (v) => !v.fleet || v.fleet === 'transvolt-ultratech')
      let list = fullRoster
      // Focus mode: a selected geofence hides every truck outside it so the map answers
      // "who is at this place" rather than showing the whole corridor.
      if (!liveOnly && !replay && s.selectedFence && s.fenceIndex.length) {
        const fenceId = s.selectedFence
        list = list.filter((v) => {
          if (v.lat == null || v.lon == null) return false
          return locate(v.lon, v.lat, s.fenceIndex)?.id === fenceId
        })
      }
      const now = Date.now()
      const isReplay = Boolean(replay?.length)
      const featureNow = isReplay ? (replay![0]?.updated_ts ?? now) : now
      const metaMap = isReplay && replayMetaRef.current?.size
        ? replayMetaRef.current
        : s.meta
      // Flags/heat/alert-events recompute at the old ~250ms cadence regardless of how
      // often the tick itself now runs for live -- see HEAVY_RECOMPUTE_MS's own comment
      // above. Replay already redraws only on an actual playhead move (the `key` dedup
      // above), so it keeps recomputing every call, unthrottled, as before.
      const heavyBucket = Math.floor(now / HEAVY_RECOMPUTE_MS)
      const dueForHeavyRecompute = isReplay || heavyBucket !== lastHeavyRecomputeBucket
      if (dueForHeavyRecompute) {
        lastHeavyRecomputeBucket = heavyBucket
        const progressForFlags = new Map(s.progress)
        if (!isReplay) {
          for (const v of list) {
            const overridden = overrideProgressWithArrival(
              v.vehicle_id, s.progress.get(v.vehicle_id), s.arrivalBoard.get(v.vehicle_id), featureNow,
            )
            if (overridden) progressForFlags.set(v.vehicle_id, overridden)
          }
        }
        const freshFlags = buildVehicleFlags(list, s.alerts, progressForFlags, metaMap, featureNow,
                                       MAP_FLAG_WINDOW_MS, s.fleetEfficiency)
        // Sticky merge: a pin must not vanish just because the bounded alerts tape evicted
        // its alert -- only because it triaged resolved or genuinely aged out. See
        // mergeStickyFlags's own comment.
        flagsRef.current = mergeStickyFlags(flagsRef.current, freshFlags, s.triage, featureNow,
                                       MAP_FLAG_WINDOW_MS)
        setHeatData(
          map,
          cellFeatures(freshCells(s.cells, now, isReplay)),
          eventFeatures(s.alerts),
        )
      }
      const flags = flagsRef.current
      // Replay frames are already frame-accurate ground truth per tick -- no real gap to
      // bridge, so they bypass the tweener entirely (undefined falls back to each
      // vehicle's raw fix in buildVehicleFeatures). Live (including liveOnly, which is
      // still real data, just rendered without hub-badge aggregation) glides every
      // vehicle from its last displayed position to its latest real fix. A vehicle whose
      // origin epoch isn't usable (e.g. both last_geo_ts and updated_ts falsy) is left out
      // of this map entirely -- buildVehicleFeatures falls back to its raw fix, same as
      // the deleted coastPosition's own `if (!origin) return raw fix` guard, rather than
      // storing a bad anchor that would freeze the display on every later real fix.
      const displayPositions = isReplay ? undefined : new Map(
        list
          .filter((v) => v.lat != null && v.lon != null)
          .flatMap((v) => {
            const origin = v.last_geo_ts || v.updated_ts
            if (!isUsableFixEpoch(origin, now)) return []
            return [[
              v.vehicle_id,
              tweenedPosition(
                tweenStoreRef.current, v.vehicle_id,
                { lat: v.lat as number, lon: v.lon as number },
                origin, now,
              ),
            ] as const]
          }),
      )
      if (!isReplay) {
        pruneTweens(tweenStoreRef.current, new Set(
          fullRoster.filter((v) => v.lat != null && v.lon != null).map((v) => v.vehicle_id),
        ))
      }
      setVehicleData(
        map,
        buildVehicleFeatures(list, s.fenceIndex, centroids,
                             featureNow, metaMap, s.breakdowns, flags, s.timsTrips,
                             // liveOnly: every truck as an icon (no hub-badge aggregation).
                             { replay: isReplay || liveOnly },
                             // Live route position, which carries the DIRECTION and so the
                             // loaded/unloaded icon. See isLaden.
                             s.progress,
                             displayPositions),
      )
      setReplayAlertData(map, isReplay ? replayAlertsRef.current : null)
      if (followRef.current && isReplay) {
        const v = replay![0]
        if (v?.lat != null && v.lon != null) {
          const c = map.getCenter()
          if (Math.abs(c.lng - v.lon) > 1e-7 || Math.abs(c.lat - v.lat) > 1e-7) {
            map.jumpTo({ center: [v.lon, v.lat] })
          }
        }
      }
    }

    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [])

  // Replay positions arrive as props; the tick reads them through a ref so a moving
  // playhead does not re-run the whole map effect sixty times a second.
  replayRef.current = liveOnly ? null : (replayFrame ?? null)
  replayMetaRef.current = liveOnly ? null : (replayMeta ?? null)
  replayAlertsRef.current = liveOnly ? null : (replayAlerts ?? null)
  onUserCameraRef.current = onUserCamera
  liveOnlyRef.current = liveOnly
  if (replayFrame && !liveOnly) replayTick.current += 1

  useEffect(() => {
    liveOnlyRef.current = liveOnly
    const map = mapRef.current
    if (!map || !styleReady.current) return
    applyLiveOnly(map, liveOnly)
  }, [liveOnly])

  useEffect(() => {
    followRef.current = Boolean(cameraFollow)
    if (!cameraFollow) return
    const map = mapRef.current
    const v = replayRef.current?.[0]
    if (!map || v?.lat == null || v.lon == null) return
    map.jumpTo({ center: [v.lon, v.lat], zoom: Math.max(map.getZoom(), 11) })
  }, [cameraFollow])

  // --------------------------------------------------------------- photorealistic 3D
  // Torn down and rebuilt rather than toggled: the overlay owns GPU resources and a
  // billed session, and leaving it attached-but-hidden keeps paying for both.
  useEffect(() =>
    useFleet.subscribe((s, prev) => {
      const map = mapRef.current
      if (!map || !styleReady.current) return
      if (s.basemapMode === prev.basemapMode && s.mapsKey === prev.mapsKey) return

      photoreal.current?.destroy()
      photoreal.current = null
      if (s.basemapMode !== 'photoreal') return

      if (!s.mapsKey) {
        useFleet.getState().addBanner({
          id: 'no-maps-key',
          tone: 'warn',
          message: 'Photorealistic 3D needs GOOGLE_MAPS_API_KEY on the serving service.',
        })
        useFleet.getState().setBasemapMode('vector')
        return
      }
      // Loaded on demand: deck.gl and the 3D-tiles loader are ~1 MB gzipped, and the
      // overwhelming majority of sessions never turn photoreal on. No reason to make
      // every operator download a renderer they are not billed for and will not use.
      const key = s.mapsKey
      void import('./photoreal').then(({ addPhotoreal }) => {
        // The mode can flip back while the chunk is in flight.
        if (useFleet.getState().basemapMode !== 'photoreal' || !mapRef.current) return
        photoreal.current = addPhotoreal(mapRef.current, key, {
          onAttribution: (c) => useFleet.getState().setAttribution(c),
          onLoad: () => useFleet.getState().dismissBanner('photoreal-failed'),
          onError: (msg) => useFleet.getState().addBanner({
            id: 'photoreal-failed', tone: 'error',
            message: `Photorealistic 3D tiles failed: ${msg}. Check the key's referrer restriction and that the Map Tiles API is enabled.`,
          }),
        })
      }).catch((e: unknown) => useFleet.getState().addBanner({
        id: 'photoreal-failed', tone: 'error',
        message: `Could not load the 3D renderer: ${String(e)}`,
      }))
    }), [])

  // ------------------------------------------------------------------ Google 2D tiles
  // Eagerly created as soon as mapsKey is known, NOT deferred to when the user picks
  // 'google2d' -- unlike photoreal's deck.gl overlay (a real ~1 MB chunk worth deferring),
  // this is one small createSession POST, and doing it upfront means the pill in
  // BasemapSwitch.tsx is enabled the first time the switcher opens rather than the user
  // hitting a disabled control that fixes itself a moment later.
  //
  // ALSO PROMOTES 'google2d' TO THE DEFAULT on first success -- the store's static initial
  // value stays 'vector' (so a session that never manages to create one, or a slow
  // connection before this POST resolves, still renders something immediately), but once a
  // session exists this upgrades the mode, same "prefer the richer option, fall back to
  // the offline-safe one" shape the 'error' handler above uses in the other direction. Only
  // fires if basemapMode is STILL 'vector' at that moment -- an operator who has already
  // picked something else (including a deliberate 'vector') by the time this resolves is a
  // real choice this must not override.
  useEffect(() => {
    let live = true
    const tryCreate = (key: string) => {
      if (!key) return
      void import('./basemaps').then(({ createGoogle2dSession }) =>
        createGoogle2dSession(key).then((ok) => {
          if (!live) return
          useFleet.getState().setGoogle2dAvailable(ok)
          if (ok && useFleet.getState().basemapMode === 'vector') {
            useFleet.getState().setBasemapMode('google2d')
          }
        }),
      )
    }
    tryCreate(useFleet.getState().mapsKey)
    const unsub = useFleet.subscribe((s, prev) => {
      if (s.mapsKey !== prev.mapsKey) tryCreate(s.mapsKey)
    })
    return () => { live = false; unsub() }
  }, [])

  // ------------------------------------------------------------------------- heat mode
  useEffect(() =>
    useFleet.subscribe((s, prev) => {
      const map = mapRef.current
      if (!map || !styleReady.current || s.heatMode === prev.heatMode) return
      if (liveOnlyRef.current) {
        setHeatMode(map, 'off')
        return
      }
      setHeatMode(map, s.heatMode)
    }), [])

  // ------------------------------------------------- the track a supervisor asked for
  useEffect(() =>
    useFleet.subscribe((s, prev) => {
      const map = mapRef.current
      if (!map || !styleReady.current || s.track === prev.track) return
      if (liveOnlyRef.current) {
        setTrackData(map, null)
        return
      }
      setTrackData(map, s.track)
      // Growing the replay trail must not fitBounds every tick — that steals pan.
      const replaying = Boolean(replayRef.current?.length)
      if (!replaying && s.track && s.track.track.length > 1) {
        const lons = s.track.track.map((p) => p.lon)
        const lats = s.track.track.map((p) => p.lat)
        map.fitBounds(
          [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]],
          { padding: { top: 70, bottom: 70, left: 70, right: 430 }, duration: 800, maxZoom: 13 },
        )
      }
    }), [])

  // --------------------------------------------------------- one-shot camera requests
  //
  // APPLIED ONCE ON MOUNT TOO, not only on the next store change. MapCanvas unmounts
  // entirely while viewMode is 'detail' (see CommandCentre's viewMode ternary), and
  // subscribe() only fires for transitions that happen AFTER it is called -- a caller
  // that does setViewMode('map') and flyTo(...) in the same handler (the Breakdowns
  // panel's "Locate", the same shape as revealVehicleOnMap) sets flyRequest BEFORE this
  // component exists to subscribe to it, so the subscription alone silently never fires
  // and the map opens whever it last was instead of at the vehicle. A truck the
  // supervisor cannot find on the map from "Locate" was the exact bug this closed.
  //
  // justFlewRef is shared with the "fly to selection" effect below: both apply on this
  // same mount (Breakdowns' Locate calls selectVehicle() and flyTo() together), and
  // flyToSelection's own camera move used to always fire right after this one and win --
  // it reads the vehicle's raw last-known telemetry position with no fallback awareness,
  // silently overriding the fallback position this effect just applied for a broken-down
  // vehicle whose live telemetry is stale or missing. Setting this lets that effect defer
  // to whatever this one just did instead of re-flying over it.
  const justFlewRef = useRef(false)
  useEffect(() => {
    const appliedNonce = { current: -1 }
    const applyFlyRequest = (req: ReturnType<typeof useFleet.getState>['flyRequest']): void => {
      const map = mapRef.current
      if (!map || !req || req.nonce === appliedNonce.current) return
      appliedNonce.current = req.nonce
      justFlewRef.current = true
      map.easeTo({ center: [req.lon, req.lat], zoom: req.zoom ?? map.getZoom(), duration: 800 })
    }
    applyFlyRequest(useFleet.getState().flyRequest)
    return useFleet.subscribe((s) => applyFlyRequest(s.flyRequest))
  }, [])

  // ------------------------------------------------------------------ fly to selection
  //
  // Same "applied once on mount" reasoning as the effect above -- selectVehicle() called
  // in the same handler as a viewMode switch is otherwise invisible to a subscription
  // that starts listening after the change already happened.
  useEffect(() => {
    const flyToSelection = (s: ReturnType<typeof useFleet.getState>, moveCamera: boolean): void => {
      const map = mapRef.current
      if (!map) return
      if (!s.selectedVehicle) {
        popupRef.current?.remove()
        popupRef.current = null
        return
      }
      const v = s.vehicles.get(s.selectedVehicle)
      if (!v || v.lat == null || v.lon == null) return
      // The effect above already flew somewhere on this same mount -- for a breakdown,
      // that was the fallback-aware position, which this v.lat/v.lon read knows nothing
      // about. Popup still shows; the camera stays wherever that fly (correctly) put it.
      if (moveCamera) {
        map.easeTo({ center: [v.lon, v.lat], zoom: Math.max(map.getZoom(), 14), duration: 700 })
      }
      popupRef.current = showVehiclePopup(map, s.selectedVehicle, [v.lon, v.lat], {
        vehicle: v,
        meta: s.meta.get(s.selectedVehicle),
        trip: resolveTimsTrip(s.selectedVehicle, s.timsTrips),
        progress: overrideProgressWithArrival(
          s.selectedVehicle, s.progress.get(s.selectedVehicle), s.arrivalBoard.get(s.selectedVehicle), Date.now(),
        ),
        flag: flagsRef.current.get(s.selectedVehicle),
      }, popupRef.current)
    }
    let lastSelected = useFleet.getState().selectedVehicle
    flyToSelection(useFleet.getState(), !justFlewRef.current)
    return useFleet.subscribe((s) => {
      if (s.selectedVehicle === lastSelected) return
      lastSelected = s.selectedVehicle
      flyToSelection(s, true)
    })
  }, [])

  return <div ref={container} className="absolute inset-0" />
}
